import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Vehicle {
  id: string
  license_plate: string
  make: string
  model: string
  year: number
  type: string
  status: string
  mileage: number
  assigned_driver: string | null
}

interface Trip {
  id: string
  vehicle_plate: string
  driver_name: string
  status: string
  start_location: string
  end_location: string
  distance_km: number
  start_time: string
}

export default function FleetManagementPage() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [trips, setTrips] = useState<Trip[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [vehiclesRes, tripsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/fleet/vehicles'),
      fetch(API_BASE + '/fleet/trips'),
      fetch(API_BASE + '/fleet/statistics')
    ])
    if (vehiclesRes.ok) setVehicles((await vehiclesRes.json()).vehicles || [])
    if (tripsRes.ok) setTrips((await tripsRes.json()).trips || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800'
      case 'in_use': return 'bg-blue-100 text-blue-800'
      case 'maintenance': return 'bg-yellow-100 text-yellow-800'
      case 'reserved': return 'bg-purple-100 text-purple-800'
      case 'completed': return 'bg-green-100 text-green-800'
      case 'in_progress': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Fleet Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Vehicle</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Vehicles</div>
              <div className="text-2xl font-bold">{statistics.total_vehicles}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Available</div>
              <div className="text-2xl font-bold text-green-600">{statistics.available}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">In Use</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.in_use}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Distance MTD</div>
              <div className="text-2xl font-bold">{statistics.total_distance_mtd?.toLocaleString()} km</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending Maint.</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.pending_maintenance}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Vehicles</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mileage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {vehicles.slice(0, 10).map(vehicle => (
                  <tr key={vehicle.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{vehicle.make} {vehicle.model}</div>
                      <div className="text-xs text-gray-500">{vehicle.license_plate} • {vehicle.year}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm capitalize">{vehicle.type}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium">{vehicle.mileage.toLocaleString()} km</div>
                      {vehicle.assigned_driver && <div className="text-xs text-gray-500">{vehicle.assigned_driver}</div>}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(vehicle.status)}`}>
                        {vehicle.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Trips</h3>
            <div className="space-y-3">
              {trips.slice(0, 8).map(trip => (
                <div key={trip.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-mono text-sm">{trip.vehicle_plate}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(trip.status)}`}>{trip.status}</span>
                  </div>
                  <div className="text-sm text-gray-600">{trip.driver_name}</div>
                  <div className="text-xs text-gray-500 mt-1">{trip.start_location} → {trip.end_location}</div>
                  {trip.distance_km > 0 && <div className="text-xs text-blue-600 mt-1">{trip.distance_km} km</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
